import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { startOfWeek, endOfWeek, format, parseISO } from "date-fns";
import { useLanguage } from "@/contexts/LanguageContext";
import { convertCurrency } from "@/lib/currencyConversion";

interface WeeklyReflectionModalProps {
  userId: string;
  open: boolean;
  onClose: () => void;
}

export const WeeklyReflectionModal = ({ userId, open, onClose }: WeeklyReflectionModalProps) => {
  const { t } = useLanguage();
  const [weekData, setWeekData] = useState<{
    totalSpent: number;
    expenseCount: number;
    dailyData: Array<{ day: string; amount: number }>;
  } | null>(null);

  useEffect(() => {
    if (open) {
      loadWeekData();
      trackEvent({ name: 'weekly_reflection_viewed' });
    }
  }, [open]);

  const loadWeekData = async () => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // Monday
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });

    // Get user's currency
    const { data: settings } = await supabase
      .from('budget_settings')
      .select('currency')
      .eq('user_id', userId)
      .maybeSingle();
    
    const userCurrency = settings?.currency || 'EUR';

    const { data: expenses } = await supabase
      .from('expenses')
      .select('amount, expense_date, currency')
      .eq('user_id', userId)
      .gte('expense_date', format(weekStart, 'yyyy-MM-dd'))
      .lte('expense_date', format(weekEnd, 'yyyy-MM-dd'));

    if (!expenses) return;

    // Convert all expenses to user's currency
    const totalSpent = expenses.reduce((sum, e) => {
      return sum + convertCurrency(Number(e.amount), e.currency || 'EUR', userCurrency);
    }, 0);
    
    // Group by day (with currency conversion)
    const dailyMap = new Map<string, number>();
    expenses.forEach((e) => {
      const day = format(parseISO(e.expense_date), 'EEE'); // Mon, Tue, etc.
      const convertedAmount = convertCurrency(Number(e.amount), e.currency || 'EUR', userCurrency);
      dailyMap.set(day, (dailyMap.get(day) || 0) + convertedAmount);
    });

    const dailyData = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => ({
      day,
      amount: dailyMap.get(day) || 0,
    }));

    setWeekData({
      totalSpent,
      expenseCount: expenses.length,
      dailyData,
    });

    // Save reflection
    await supabase.from('weekly_reflections').upsert({
      user_id: userId,
      week_start: format(weekStart, 'yyyy-MM-dd'),
      week_end: format(weekEnd, 'yyyy-MM-dd'),
      total_spent: totalSpent,
      expense_count: expenses.length,
      viewed_at: new Date().toISOString(),
    });
  };

  const handleClose = () => {
    onClose();
  };

  if (!weekData) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl">{t('weekly.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-secondary rounded-lg">
              <div className="text-3xl font-bold text-foreground">
                ${weekData.totalSpent.toFixed(2)}
              </div>
              <div className="text-sm text-muted-foreground mt-1">{t('weekly.totalSpent')}</div>
            </div>
            <div className="text-center p-4 bg-secondary rounded-lg">
              <div className="text-3xl font-bold text-foreground">
                {weekData.expenseCount}
              </div>
              <div className="text-sm text-muted-foreground mt-1">{t('weekly.expensesLogged')}</div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">{t('weekly.dailySpending')}</h3>
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={weekData.dailyData}>
                <XAxis 
                  dataKey="day" 
                  tick={{ fontSize: 12 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis hide />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px',
                  }}
                  formatter={(value: number) => `$${value.toFixed(2)}`}
                />
                <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-purple-500/10 text-purple-600 dark:text-purple-400 p-4 rounded-lg text-center">
            <p className="font-medium">{t('weekly.bonusProgress')}</p>
          </div>

          <Button onClick={handleClose} className="w-full">
            {t('weekly.gotIt')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
